import { useState, useRef, type ChangeEvent, type FormEvent, type DragEvent } from 'react';
import {
  UploadCloud,
  FileText,
  Image as ImageIcon,
  CheckCircle2,
  X,
  Sparkles,
  Plus,
  Trash2,
} from 'lucide-react';
import { Drawer, Field, useToast } from './ui';
import {
  type NewDrawingPayload,
  useUploadMultipleElevations,
  formatFileSize,
} from '../lib/project-drawings';

interface Props {
  projectId: string;
  projectName?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

interface StagedFile {
  id: string;
  file: File;
  previewUrl: string;
  title: string;
  description: string;
}

export default function ElevationUploadDrawer({
  projectId,
  projectName,
  onClose,
  onSuccess,
}: Props) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [commonNotes, setCommonNotes] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  const batchUploadMutation = useUploadMultipleElevations(projectId);

  function processFiles(files: FileList | File[] | null) {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    fileArray.forEach((file) => {
      const cleanName = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
      const reader = new FileReader();
      reader.onload = () => {
        const previewUrl = reader.result as string;
        setStagedFiles((prev) => [
          ...prev,
          {
            id: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            file,
            previewUrl,
            title: cleanName,
            description: '',
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  }

  function handleFileInputChange(e: ChangeEvent<HTMLInputElement>) {
    processFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    processFiles(e.dataTransfer.files);
  }

  function removeStagedFile(id: string) {
    setStagedFiles((prev) => prev.filter((f) => f.id !== id));
  }

  function updateStagedTitle(id: string, newTitle: string) {
    setStagedFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, title: newTitle } : f)),
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (stagedFiles.length === 0) {
      toast.show('Please select at least one 3D image or elevation file to upload', true);
      return;
    }

    setLoading(true);
    try {
      const payloads: NewDrawingPayload[] = stagedFiles.map((staged) => ({
        projectId,
        category: 'elevation',
        title: staged.title.trim() || staged.file.name,
        fileName: staged.file.name,
        fileType: staged.file.type || 'application/octet-stream',
        fileSize: staged.file.size,
        fileUrl: staged.previewUrl,
        description: staged.description.trim() || commonNotes.trim() || undefined,
      }));

      await batchUploadMutation.mutateAsync(payloads);

      toast.show(
        stagedFiles.length === 1
          ? '3D Elevation file uploaded successfully'
          : `${stagedFiles.length} 3D Elevation files uploaded successfully`,
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Failed to upload elevation files', true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Drawer
      title="Upload 3D Elevations & Renders"
      description={projectName ? `Upload 3D perspectives, renders, and elevation documents for ${projectName}` : 'Upload multiple 3D images, perspective views, and architectural elevation files'}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={
        stagedFiles.length > 1
          ? `Upload ${stagedFiles.length} Elevation Files`
          : stagedFiles.length === 1
          ? 'Upload Elevation File'
          : 'Upload 3D Elevations'
      }
      busy={loading || batchUploadMutation.isPending}
      wide
    >
      {/* Upload Info Banner */}
      <div
        style={{
          padding: '12px 14px',
          borderRadius: 'var(--radius-sm)',
          background: '#f0f9ff',
          border: '1px solid #bae6fd',
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 13,
          color: '#0369a1',
        }}
      >
        <Sparkles size={18} style={{ flexShrink: 0, color: '#0284c7' }} />
        <span>
          <strong>Multiple 3D Renders Supported:</strong> You can select and upload multiple 3D perspective views, front/rear elevations, and walkthrough documents at once.
        </span>
      </div>

      {/* File Dropzone Area */}
      <div style={{ marginBottom: 20 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf,application/pdf"
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />

        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${isDragging ? '#0284c7' : 'var(--border)'}`,
            borderRadius: 'var(--radius)',
            background: isDragging ? '#f0f9ff' : 'var(--surface-2)',
            padding: '28px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'var(--surface)',
              color: '#0284c7',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 10,
              boxShadow: 'var(--shadow-xs)',
            }}
          >
            <UploadCloud size={24} />
          </div>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 4 }}>
            Click to choose or drag & drop multiple 3D files
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-soft)', maxWidth: 420, margin: '0 auto' }}>
            Supports 3D perspective renderings (JPG, PNG, WEBP), Front & Side elevation photos, and CAD/PDF drawings
          </div>
        </div>
      </div>

      {/* Selected / Staged Files Grid */}
      {stagedFiles.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <label className="field-label" style={{ fontWeight: 600, margin: 0 }}>
              Files Ready for Upload ({stagedFiles.length}):
            </label>
            <button
              type="button"
              className="btn btn-auto btn-sm btn-ghost"
              onClick={() => fileInputRef.current?.click()}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}
            >
              <Plus size={13} />
              <span>Add More Files</span>
            </button>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              maxHeight: 280,
              overflowY: 'auto',
              paddingRight: 4,
            }}
          >
            {stagedFiles.map((item, idx) => {
              const isImg = item.file.type.startsWith('image/') || item.previewUrl.startsWith('data:image/');
              return (
                <div
                  key={item.id}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--surface)',
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  {/* Thumbnail */}
                  {isImg ? (
                    <img
                      src={item.previewUrl}
                      alt={item.title}
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 'var(--radius-xs)',
                        objectFit: 'cover',
                        border: '1px solid var(--border)',
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 'var(--radius-xs)',
                        background: '#fee2e2',
                        color: '#dc2626',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <FileText size={24} />
                    </div>
                  )}

                  {/* Title & Metadata input */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#0284c7',
                          background: '#f0f9ff',
                          padding: '1px 6px',
                          borderRadius: 4,
                        }}
                      >
                        #{idx + 1}
                      </span>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => updateStagedTitle(item.id, e.target.value)}
                        style={{
                          flex: 1,
                          fontSize: 13,
                          padding: '4px 8px',
                          height: 28,
                          borderRadius: 'var(--radius-xs)',
                          border: '1px solid var(--border)',
                          background: 'var(--surface-2)',
                        }}
                      />
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-soft)' }}>
                      {item.file.name} • {formatFileSize(item.file.size)}
                    </div>
                  </div>

                  {/* Remove Button */}
                  <button
                    type="button"
                    className="icon-btn btn-danger"
                    onClick={() => removeStagedFile(item.id)}
                    title="Remove this file"
                    style={{ width: 28, height: 28 }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* General Description / Notes */}
      <Field label="Batch Remarks / Notes (Optional)">
        <textarea
          value={commonNotes}
          onChange={(e) => setCommonNotes(e.target.value)}
          rows={2}
        />
      </Field>
    </Drawer>
  );
}
